import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Item Categories (PART 5, MD-04) — the tree items are filed under.
 *
 * The list route returns the WHOLE tree flat (parentId links); the client
 * builds the tree — the catalog is small master data, not a paged document
 * list. Sibling-unique names (BR-MD-04-02), max 5 levels (BR-MD-04-04), no
 * cycles (BR-MD-04-03). Items reference the category ID — never the name
 * (the MD-04 join-by-name defect, fixed as the spec proposes).
 *
 * Permission area: `inventory` — PART 16.2.2 row 3. Matrix 16.4.2:
 * Administrator RCUDA, Accountant R, AP Clerk R.
 */
// ── The fixed sentences of MD-04 ────────────────────────────────────────────
export const CATEGORY_MESSAGES = {
    /** ERR-MD-04-01 / BR-MD-04-01. */
    nameRequired: 'Type a name for this group.',
    /** ERR-MD-04-02 (BR-MD-04-02). */
    siblingTaken: (parent, name) => `${parent} already has a group called ${name}.`,
    /** BR-MD-04-02 at the top level (no spec-fixed wording for the rootless case). */
    topLevelTaken: (name) => `There is already a top-level group called ${name}.`,
    /** Field 0: codes are unique (no spec-fixed wording; the MD-03 pattern). */
    codeTaken: (code, name) => `${code} is already used by ${name}.`,
    /** ERR-MD-04-03 / BR-MD-04-03. */
    cycle: (moved, into) => `${moved} cannot be moved inside ${into}, because ${into} is already inside ${moved}.`,
    /** ERR-MD-04-04 / BR-MD-04-04. */
    tooDeep: 'Groups can go 5 levels deep. This would be 6.',
    /** ERR-MD-04-05 / BR-MD-04-05 — names both counts. */
    inUse: (name, items, groups) => `${name} holds ${items} ${items === 1 ? 'item' : 'items'} and ${groups} ${groups === 1 ? 'group' : 'groups'} and cannot be deleted. Switch it off instead.`,
    /** BR-MD-04-07 (no spec-fixed wording; the shared inactive pattern). */
    inactive: (name) => `${name} is switched off and cannot be picked on a new item.`,
};
/**
 * D46 — the category family's structured refusals (the d46masters wave):
 * key + params beside the composed English sentence, so a refusal that names
 * a group, a code or the two in-use counts reaches an Arabic reader in
 * Arabic. One builder per parameterised member the API raises inside an HTTP
 * exception body — the raise sites throw THESE, and each `message` is
 * composed by the `CATEGORY_MESSAGES` twin so the two cannot drift (parity
 * pinned member by member in `apps/api/test/refusal-census.spec.ts`;
 * both-language renders in `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `inUse` travels BOTH raw counts (items and child groups); the dictionaries
 * pluralise each noun independently (en mirrors the sentence's own two
 * one/other ternaries, ar carries a full six-category set per count).
 * `cycle`'s two params are both names — the self-parent raise site passes
 * the same name twice, which is the twin's own behaviour, not a defect.
 * `inactive` is deliberately NOT here: no API raise site names
 * `CATEGORY_MESSAGES.inactive` — the items service raises that wall through
 * a local RETYPED copy of the sentence (`CATEGORY_INACTIVE` in
 * `items.service.ts`, the class the census header marks as NFR-01's
 * territory), so a builder here would be a raise shape with no raise site.
 */
export const CATEGORY_REFUSALS = {
    siblingTaken: (parent, name) => ({
        message: CATEGORY_MESSAGES.siblingTaken(parent, name),
        key: 'cats.errSiblingTaken',
        params: { parent, name },
    }),
    topLevelTaken: (name) => ({
        message: CATEGORY_MESSAGES.topLevelTaken(name),
        key: 'cats.errTopLevelTaken',
        params: { name },
    }),
    codeTaken: (code, name) => ({
        message: CATEGORY_MESSAGES.codeTaken(code, name),
        key: 'cats.errCodeTaken',
        params: { code, name },
    }),
    cycle: (moved, into) => ({
        message: CATEGORY_MESSAGES.cycle(moved, into),
        key: 'cats.errCycle',
        params: { moved, into },
    }),
    inUse: (name, items, groups) => ({
        message: CATEGORY_MESSAGES.inUse(name, items, groups),
        key: 'cats.errInUse',
        params: { name, items, groups },
    }),
};
/**
 * The six GEN-170 accent hexes MD-04 field 1a offers as QUICK PICKS. Any
 * `#rrggbb` is accepted (CAT-03, owner decision 2026-10-12) — these are the
 * swatches, not the whole palette.
 */
export const CATEGORY_COLOURS = [
    '#0e9aa1',
    '#3b82f6',
    '#8b5cf6',
    '#f59e0b',
    '#10b981',
    '#ef4444',
];
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ItemCategorySchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    /** S5 / CAT-04: optional Arabic name; the Arabic UI shows `nameAr ?? name`. */
    nameAr: z.string().nullable(),
    colour: z.string().nullable(),
    /** NULL = top level. */
    parentId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    description: z.string().nullable(),
    defaultUnitId: z.uuid().nullable(),
    defaultTax: z.string().nullable(),
    /**
     * INV-08 §12: default inventory account CODE for items filed here that
     * carry none of their own. NULL = 1131 Raw Materials.
     */
    inventoryAccountCode: z.string().nullable(),
    isActive: z.boolean(),
    /** Field 9: items in this category AND every category beneath it. */
    itemCount: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
/** CAT-03: any #rrggbb (lower-cased to match the column's CHECK), not only the six quick picks. */
const ColourSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^#[0-9a-f]{6}$/);
export const CreateItemCategoryRequestSchema = z
    .object({
    /** CAT-02: omitted = "Auto" — the API allocates the next CAT-### for the tenant. */
    code: z.string().trim().min(1).max(20).optional(),
    name: z.string().trim().min(1, CATEGORY_MESSAGES.nameRequired).max(80),
    nameAr: z.string().trim().min(1).max(80).optional(),
    colour: ColourSchema.optional(),
    parentId: z.uuid().optional(),
    sortOrder: z.number().int().min(0).max(999).default(0),
    description: z.string().trim().max(400).optional(),
    defaultUnitId: z.uuid().optional(),
    defaultTax: z.string().trim().max(60).optional(),
    inventoryAccountCode: z.string().trim().min(1).max(20).optional(),
})
    .strict();
export const UpdateItemCategoryRequestSchema = z
    .object({
    code: z.string().trim().min(1).max(20).optional(),
    name: z.string().trim().min(1, CATEGORY_MESSAGES.nameRequired).max(80).optional(),
    /** null clears the Arabic name (the Arabic UI falls back to `name`). */
    nameAr: z.string().trim().min(1).max(80).nullable().optional(),
    colour: ColourSchema.nullable().optional(),
    /** null moves the group to the top level (Move to… takes children along). */
    parentId: z.uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    description: z.string().trim().max(400).nullable().optional(),
    defaultUnitId: z.uuid().nullable().optional(),
    defaultTax: z.string().trim().max(60).nullable().optional(),
    inventoryAccountCode: z.string().trim().min(1).max(20).nullable().optional(),
})
    .strict();
const categoryParams = z.object({ categoryId: z.uuid() }).strict();
export const itemCategoriesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/item-categories',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ categories: ItemCategorySchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The whole tree, flat (inventory.view). Item counts are inclusive of descendants (MD-04 field 9).',
    },
    get: {
        method: 'GET',
        path: '/api/item-categories/:categoryId',
        pathParams: categoryParams,
        responses: {
            200: ItemCategorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One category (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/item-categories',
        body: CreateItemCategoryRequestSchema,
        responses: {
            201: ItemCategorySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New group (inventory.create). No code = Auto: the next CAT-### is allocated (CAT-02). Sibling-duplicate names refused (ERR-MD-04-02); a sixth level refused (ERR-MD-04-04).',
    },
    update: {
        method: 'PATCH',
        path: '/api/item-categories/:categoryId',
        pathParams: categoryParams,
        body: UpdateItemCategoryRequestSchema,
        responses: {
            200: ItemCategorySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit / Move to… (inventory.edit). A group cannot be moved inside its own child (ERR-MD-04-03); children move with it (BR-MD-04-10).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/item-categories/:categoryId/deactivate',
        pathParams: categoryParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ItemCategorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch off (inventory.edit). Switches off nothing else — its items stay active (BR-MD-04-06).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/item-categories/:categoryId/reactivate',
        pathParams: categoryParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ItemCategorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (inventory.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/item-categories/:categoryId',
        pathParams: categoryParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — only a group holding no items and no child groups (ERR-MD-04-05 names both counts).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=item-categories.js.map